<template>
	<button
		class="tag-button"
		:class="{
			'-active': active,
			'-disabled': !enabled,
			'-highlighted': highlighted,
			'-non-clickable': !clickable,
			'-squared': isSquared
		}"
		:disabled="!enabled"
		@click="handleClick"
	>
		<Icon
			v-if="iconPosition === 'before' && !hideIcon"
			:icon-name="iconName"
			:size="iconSize"
			class="tag-button__icon -before"
		/>
		<span
			v-if="label"
			class="tag-button__label"
			:class="{
				'-text-right': iconPosition === 'after'
			}"
		><slot name="label">{{ label }}</slot></span>
		<Icon
			v-if="iconPosition === 'after'"
			:icon-name="iconName"
			:size="iconSize"
			class="tag-button__icon -after"
		/>
		<span
			v-if="label && labelShadow"
			class="tag-button__shadow"
			:class="{'-shifted': editable}"
		/>
		<span
			v-if="editable"
			class="tag-button__menu"
		>
			<ContextMenu :menu-items="menuItems">
				<template #toggle>
					<Icon
						icon-name="context-menu"
						size="lg"
					/>
				</template>
			</ContextMenu>
		</span>
	</button>
</template>

<script>
import ContextMenu from '@components/context-menu/ContextMenu';
export default {
	name:       'TagButton',
	components: { ContextMenu },
	props:      {
		buttonId: {
			type: [String, Number],
		},
		iconPosition: {
			type:      String,
			default:   'before',
			validator: (v) => (['before', 'after'].includes(v)),
		},
		iconName: {
			type: String,
		},
		iconSize: {
			type:    String,
			default: 'md',
		},
		label: {
			type:    String,
			default: null,
		},
		clickable: {
			type:    Boolean,
			default: true,
		},
		editable: {
			type:    Boolean,
			default: false,
		},
		isSquared: {
			type: Boolean,
		},
		active: {
			type: Boolean, // border around button
		},
		highlighted: {
			type: Boolean, // yellow background
		},
		enabled: {
			type:    Boolean,
			default: true,
		},
		labelShadow: {
			type:    Boolean,
			default: true,
		},
		hideIcon: {
			type:    Boolean,
			default: false,
		},
	},
	computed: {
		menuItems() {
			return [
				{
					id:      'edit',
					label:   this.transUcfirst('commons.edit'),
					handler: this.onEdit,
					icon:    'pen',
				},
				{
					id:      'delete',
					label:   this.transUcfirst('commons.delete'),
					class:   'danger',
					handler: this.onDelete,
					icon:    'bin',
				},
			];
		},
	},
	methods: {
		handleClick() {
			this.$emit('click');
		},
		onEdit() {
			this.$emit('edit', this.buttonId);
		},
		onDelete() {
			this.$emit('delete', this.buttonId);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/fonts';

.tag-button {
	position: relative;
	display: inline-flex;
	width: 100%;
	padding: 11px 7px;
	border: 1px solid var(--color-grey-800);
	border-radius: 2px;
	height: 48px;
	gap: $sp3;
	align-items: center;
	background-color: var(--color-grey-800);

	&:disabled {
		cursor: initial;
		border-color: var(--color-grey-850);
		background: var(--color-grey-850);
		color: var(--color-grey-700);
	}

	&.-non-clickable {
		cursor: initial;
	}

	// &.-active {}

	&.-highlighted {
		background: var(--color-hype-yellow);
		color: var(--color-text-on-hype-yellow);

		@media (hover: hover) {
			&:hover:not(:disabled) {
				background: var(--color-hype-yellow--active);

				.tag-button__shadow {
					background: linear-gradient(270deg, var(--color-hype-yellow--active) 0%, rgba(56, 57, 61, 0) 100%);
				}
			}
		}
	}

	&.-squared {
		width: 90px;
		height: 80px;
		padding: 0 16px;
		border-radius: 4px;
		justify-content: center;
		flex-direction: column;
		align-items: center;
		font-size: 18px;

		@include size(md-up) {
			width: 64px;
			height: 64px;
		}
	}

	@media (hover: hover) {
		&:hover:not(:disabled, .-non-clickable) {
			background: var(--color-grey-700);

			.tag-button__shadow {
				background: linear-gradient(270deg, var(--color-grey-700) 0%, rgba(56, 57, 61, 0) 100%);
			}
		}

		&:hover:not(:disabled) {
			.tag-button__menu::v-deep .context-menu-toggle {
				visibility: visible;
			}
		}
	}
}

.tag-button__label {
	@include text-md-2;
	flex-grow: 1;
	white-space: nowrap;
	overflow: hidden;

	&.-text-right {
		text-align: right;
	}

	// Squared variant adjustments
	.tag-button.-squared & {
		flex: 0 1 auto;
		max-width: 100%;
		margin-top: 7px;
		font-size: 14px;
	}
}

.tag-button__shadow {
	position: absolute;
	right: 5px;
	width: 25px;
	height: 40px;
	background: linear-gradient(270deg, var(--color-grey-800) 0%, rgba(56, 57, 61, 0) 100%);

	&.-shifted {
		right: 31px;
	}

	.tag-button:disabled & {
		background: linear-gradient(270deg, var(--color-grey-850) 0%, rgba(56, 57, 61, 0) 100%);
	}

	.tag-button.-highlighted & {
		background: linear-gradient(270deg, var(--color-hype-yellow) 0%, rgba(56, 57, 61, 0) 100%);
	}

	.tag-button.-squared & {
		right: 10px;
		bottom: 10px;
	}
}

.tag-button__menu {
	&::v-deep .context-menu {
		&-toggle {
			visibility: hidden;
		}

		&.open .context-menu-toggle {
			visibility: visible;
		}
	}
}
</style>
